import { useSnapshot } from 'valtio'
import { uiState } from '../data'
import { AnimatePresence, motion, useAnimation } from 'framer-motion'
import { isMobile } from 'react-device-detect'
import { useEffect, useRef } from 'react'
import { windowSizeState } from '../utils/windowSize'

const Homepage = ({ event }) => {
  const {
    visible,
    zh,
    touchStartY,
    touchCurrentY,
    scrollCount,
    prevScrollY,
    isScrolling,
    canScroll,
    canTouch,
    logoDuration,
    showTip,
    showEndTipAnimation,
    showTipAnimation,
    isEnterHover,
    timer,
    showScrollText,
    prevScrollCount
  } = useSnapshot(uiState[event])
  const { isMusicPlaying } = useSnapshot(uiState.music)
  const { detailPage, iconState } = useSnapshot(uiState)
  const containerRef = useRef()
  const textRef = useRef()
  const tipRef = useRef()
  const { mobileMode, phoneMode, isLandscapePhone, padMode } =
    useSnapshot(windowSizeState)

  const goVr = () => {
    window.onEvent('team-go:meow')
  }

  const scrollHandler = (e) => {
    // 向下滑動
    if (
      !isScrolling &&
      e.deltaY < 150 &&
      e.deltaY > 0 &&
      scrollCount < 4 &&
      canScroll
    ) {
      uiState[event].isScrolling = true

      const currentScrollY = e.deltaY

      if (currentScrollY >= prevScrollY) {
        uiState[event].scrollCount = Number(uiState[event].scrollCount) + 1
      }
      uiState[event].prevScrollY = currentScrollY

      setTimeout(() => {
        uiState[event].isScrolling = false
      }, 500)
    }

    // 向上滑動
    if (!isScrolling && e.deltaY > -150 && e.deltaY < 0 && scrollCount > 0) {
      uiState[event].isScrolling = true
      const currentScrollY = e.deltaY

      if (currentScrollY >= prevScrollY) {
        uiState[event].scrollCount = Number(uiState[event].scrollCount) - 1
      }
      uiState[event].prevScrollY = currentScrollY
      setTimeout(() => {
        uiState[event].isScrolling = false
      }, 500)
    }
  }

  const musicController = () => {
    uiState.music.isMusicPlaying = !uiState.music.isMusicPlaying
  }

  const touchStartHandler = (e) => {
    uiState[event].touchStartY = e.touches[0].clientY
  }

  const touchMoveHandler = (e) => {
    uiState[event].touchCurrentY = e.touches[0].clientY
  }

  useEffect(() => {
    document.addEventListener(
      'touchstart',
      canScroll ? touchStartHandler : undefined
    )
    document.addEventListener(
      'touchmove',
      canScroll ? touchMoveHandler : undefined
    )

    return () => {
      document.removeEventListener('touchstart', touchStartHandler)
      document.removeEventListener('touchmove', touchMoveHandler)
    }
  }, [canScroll])

  useEffect(() => {
    if (canTouch && !detailPage.visible) {
      uiState[event].canTouch = false
      if (
        touchCurrentY - touchStartY > 80 &&
        scrollCount > 0 &&
        scrollCount !== 4
      ) {
        uiState[event].scrollCount = Number(uiState[event].scrollCount) - 1
      } else if (touchCurrentY - touchStartY > 120 && scrollCount === 4) {
        uiState[event].scrollCount = Number(uiState[event].scrollCount) - 1
      } else if (touchCurrentY - touchStartY < -80 && scrollCount < 4) {
        uiState[event].scrollCount = Number(uiState[event].scrollCount) + 1
      }

      setTimeout(() => {
        uiState[event].canTouch = true
      }, 500)
    }
  }, [touchStartY, touchCurrentY])

  useEffect(() => {
    if (scrollCount === 4) {
      uiState[event].showTipAnimation = false
      setTimeout(() => {
        if (scrollCount === 4) {
          uiState[event].showTip = false

          uiState[event].showEndTipAnimation = true
        }
      }, 1000)
      setTimeout(() => {
        if (textRef.current) {
          textRef.current.scrollTop = 0
        }
      }, 3300)
    } else {
      if (logoDuration) {
        uiState[event].showTip = true
        uiState[event].showTipAnimation = true
      }
      uiState[event].showEndTipAnimation = false
    }
  }, [scrollCount])

  const controls = useAnimation()

  const tipAnimation = async () => {
    await controls.start({
      x: '-50%',
      top: ['18%', '60%', '60%'],
      opacity: [1, 1, 0]
    })
  }

  useEffect(() => {
    let animationInterval

    if (showTipAnimation) {
      tipAnimation() // 手動觸發第一次執行

      animationInterval = setInterval(() => {
        tipAnimation() // 每隔 1500 毫秒觸發動畫
      }, 1500)
    }

    return () => {
      if (animationInterval && logoDuration) {
        clearInterval(animationInterval)
      }
    }
  }, [showTipAnimation])

  useEffect(() => {
    setTimeout(() => {
      if (showEndTipAnimation) {
        uiState[event].showEndTipAnimation = false
      }
    }, 2000)
  }, [showEndTipAnimation])

  const shineVariants = {
    show: {
      opacity: [0.2, 0.4, 0.6, 0.8, 1, 0.8, 0.6, 0.4, 0.2],
      transition: {
        duration: 2.5,
        repeat: Infinity,
        ease: 'linear'
      },
      willChange: 'transform',
      layout: 'position'
    },
    hide: {
      opacity: [0]
    }
  }

  useEffect(() => {
    let intervalId

    const checkScrollCount = () => {
      if (scrollCount === prevScrollCount) {
        uiState.homepage.timer = Number(uiState.homepage.timer) + 0.5

        if (timer === 10) {
          uiState.homepage.showScrollText = true
          uiState.homepage.timer = 0
          clearInterval(intervalId)
        }
      } else {
        uiState.homepage.showScrollText = false
        uiState.homepage.timer = 0
        uiState.homepage.prevScrollCount = scrollCount
      }
    }

    intervalId = setInterval(
      canScroll && canTouch && scrollCount !== 4 && checkScrollCount,
      500
    )

    return () => {
      clearInterval(intervalId)
    }
  }, [scrollCount, timer, prevScrollCount, canScroll, canTouch])

  return (
    <>
      {visible && (
        <div className='fixed top-0 left-0 screen-setting bg-black z-[99] justify-center items-center hidden landscapePhone:flex'>
          <div className=' w-[60%] aspect-[1/0.53] NotoSansB text-[20px] flex  justify-center items-center border-[3px] border-white p-[30px] rounded-[30px]'>
            <div className='w-[calc(100%-210px)] mr-[25px]'>
              <img
                src='../startImages/animated.png'
                alt='橫幅手機提示'
                className='w-full object-contain'
              />
            </div>
            <div className='text-white  w-[140px] '>
              <div>
                請以<span className=' text-mainOrage-100'>直式</span>
              </div>
              <div>繼續瀏覽網頁</div>
            </div>
          </div>
        </div>
      )}
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className='screen-setting absolute top-0 left-0  bg-bgColor-100 '
            onWheel={scrollHandler}
            ref={containerRef}
          >
            <AnimatePresence>
              {(scrollCount === 0 ||
                scrollCount === 1 ||
                scrollCount === 2 ||
                scrollCount === 3) && (
                <motion.img
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  src='../startImages/bg_line.gif'
                  alt='背景會動的線'
                  className=' absolute top-0 left-0 w-full h-full object-cover'
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {(scrollCount === 0 ||
                scrollCount === 1 ||
                scrollCount === 2) && (
                <motion.img
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8 }}
                  src='../startImages/bg_text.gif'
                  alt='背景會動的字'
                  className=' absolute top-0 left-0 w-full h-full z-20 object-cover'
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {scrollCount === 0 && !canScroll && !canTouch && (
                <motion.img
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: !logoDuration ? 0.8 : 0 }}
                  src={`../startImages/logo_v0.png?timestamp=${Date.now()}`}
                  alt='logo'
                  className=' absolute top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 w-[45%] landscapePad:w-[65%] portraitPad:w-[85%]  object-contain portraitBigPh:min-w-[85%]'
                  onAnimationComplete={() => {
                    setTimeout(() => {
                      uiState[event].logoDuration = true
                      uiState[event].canScroll = true
                      uiState[event].showTip = true
                      uiState[event].showTipAnimation = true
                      uiState[event].canTouch = true
                    }, 2000)
                  }}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {scrollCount === 0 && (
                <motion.img
                  initial={{ opacity: 0 }}
                  animate={{ opacity: canScroll || canTouch ? 1 : 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: logoDuration ? 0.8 : 0 }}
                  src='../startImages/logo_n_v0.png'
                  alt='logo'
                  className=' absolute top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 w-[45%] landscapePad:w-[65%] portraitPad:w-[85%]  object-contain portraitBigPh:min-w-[85%]'
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {(scrollCount === 0 || scrollCount === 1) &&
                showTip &&
                !mobileMode && (
                  <motion.img
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8 }}
                    src='../startImages/right_text_pc.png'
                    alt='右邊的字'
                    className=' absolute top-1/2 -translate-y-1/2 right-0 h-full z-30 object-contain'
                  />
                )}
            </AnimatePresence>
            <AnimatePresence>
              {showTip && (
                <div className=' absolute bottom-0 -translate-y-1/2 left-1/2 -translate-x-1/2 h-[80px] z-30 object-contain portraitBigPh:h-[55px] landscapePhone:h-[55px] portraitPad:bottom-[5%] portraitBigPh:bottom-[3%] flex items-center w-[300px] portraitBigPh:w-[250px]  justify-center '>
                  <AnimatePresence>
                    {showScrollText && (
                      <motion.div
                        className='NotoSerifB portraitBigPh:text-[14px]'
                        variants={shineVariants}
                        initial='hide'
                        animate='show'
                        exit={{ opacity: 0 }}
                      >
                        {isMobile ? '－滑動畫面' : '－請以滾輪'}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <div className='h-full relative w-[75px] portraitBigPh:w-[60px] flex justify-center'>
                    <motion.img
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.8 }}
                      src='../startImages/tips_line.png'
                      alt='滑鼠滾動提示'
                      ref={tipRef}
                      className=' h-full object-contain'
                    />

                    <motion.div
                      className='absolute  left-[50.07%] -translate-x-1/2  z-50 w-[16px]  aspect-square bg-mainOrange-100 rounded-[50%] portraitBigPh:w-[10px] landscapePhone:w-[10px]'
                      initial={{ top: '18%', x: '-50%' }}
                      animate={controls}
                      transition={{ duration: 1 }}
                    ></motion.div>
                  </div>

                  <div className=' absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2'>
                    <svg
                      width={phoneMode || isLandscapePhone ? '36' : '44'}
                      height={phoneMode || isLandscapePhone ? '63' : '84'}
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <motion.rect
                        x='2'
                        y='2'
                        width={phoneMode || isLandscapePhone ? '32' : '40'}
                        height={phoneMode || isLandscapePhone ? '59' : '80'}
                        rx='50'
                        ry={phoneMode || isLandscapePhone ? '16' : '20'}
                        fill='none'
                        stroke='#DA691D'
                        strokeWidth={2}
                        initial={{ pathLength: 0 }}
                        animate={{
                          pathLength:
                            scrollCount === 1
                              ? 0.25
                              : scrollCount === 2
                              ? 0.5
                              : scrollCount === 3
                              ? 0.75
                              : scrollCount === 4
                              ? 1
                              : 0
                        }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1 }}
                      />
                    </svg>
                  </div>
                  <AnimatePresence>
                    {showScrollText && (
                      <motion.div
                        className='NotoSerifB portraitBigPh:text-[14px]'
                        variants={shineVariants}
                        initial='hide'
                        animate='show'
                        exit={{ opacity: 0 }}
                      >
                        繼續瀏覽－
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {showEndTipAnimation && (
                <motion.div className=' absolute bottom-0 -translate-y-1/2 left-1/2 -translate-x-1/2 h-[80px] z-30 object-contain portraitBigPh:h-[55px] landscapePhone:h-[55px] portraitPad:bottom-[5%] portraitBigPh:bottom-[3%]'>
                  <img
                    src={`../startImages/end.png?timestamp=${Date.now()}`}
                    alt='提示結束動畫'
                    className='h-full object-contain'
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {(scrollCount === 0 || scrollCount === 1) &&
                showTip &&
                mobileMode && (
                  <motion.img
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8 }}
                    src='../startImages/bottom_text_ph.png'
                    alt='行動版底部文字'
                    className=' absolute bottom-0 -translate-y-1/2 left-1/2 -translate-x-1/2 w-full z-30 object-contain '
                  />
                )}
            </AnimatePresence>
            <AnimatePresence>
              {scrollCount === 4 && mobileMode && (
                <motion.div
                  className={`fixed z-[80] top-[3%]  w-[45px] object-contain pointer-events-auto  portraitBigPh:w-[30px]  right-[20px] portraitBigPh:right-[12px]`}
                  animate={{
                    rotate: iconState === 135 ? '135deg' : 0,
                    top: detailPage.visible ? '92%' : '3%'
                  }}
                  transition={{
                    top: { duration: 1 },
                    rotate: { duration: 0.5 }
                  }}
                >
                  <motion.img
                    src='../startImages/thx_icon.png'
                    alt='名單toggle按鈕'
                    className='w-full object-contain'
                    onMouseEnter={() => {
                      uiState.mouseStyle.showOrangeSpot = false
                    }}
                    onMouseLeave={() => {
                      uiState.mouseStyle.showOrangeSpot = true
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      uiState[event].scrollCount = 4
                      if (detailPage.visible) {
                        uiState.detailPage.visible = false
                      } else {
                        uiState.detailPage.visible = true
                      }
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.8,
                      delay: showTip ? 5 : 0
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {scrollCount === 4 && !mobileMode && (
                <motion.div
                  className={`fixed z-[80] top-[50px]  w-[45px] object-contain pointer-events-auto landscapePad:top-[3%]  left-[50px]`}
                  animate={{
                    rotate: iconState === 135 ? '135deg' : 0,
                    top: mobileMode && detailPage.visible ? '92%' : '30px',
                    left:
                      !padMode && detailPage.visible
                        ? '33%'
                        : !padMode && !detailPage.visible
                        ? '3%'
                        : padMode && detailPage.visible
                        ? '43.5%'
                        : padMode && !detailPage.visible
                        ? '3%'
                        : '0%'
                  }}
                  transition={{
                    top: { duration: 1 },
                    left: { duration: 1 },
                    rotate: { duration: 0.5 }
                  }}
                >
                  <motion.img
                    src='../startImages/thx_icon.png'
                    alt='名單toggle按鈕'
                    className='w-full object-contain'
                    onMouseEnter={() => {
                      uiState.mouseStyle.showOrangeSpot = false
                    }}
                    onMouseLeave={() => {
                      uiState.mouseStyle.showOrangeSpot = true
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      uiState[event].scrollCount = 4
                      if (detailPage.visible) {
                        uiState.detailPage.visible = false
                      } else {
                        uiState.detailPage.visible = true
                      }
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.8,
                      delay: showTip ? 5 : 0
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {scrollCount === 4 && (
                <motion.div
                  className=' absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40   w-[85%] h-[65%] portraitPad:h-[70%] flex flex-col whitespace-pre-wrap laptop:w-[90%] landscapePad:w-[90%] landscapePad:h-[70%] landscapePad:top-[12%] landscapePad:-translate-y-0 portraitBigPh:w-[85%] portraitBigPh:h-full portraitBigPh:top-0 portraitBigPh:translate-y-0   portraitBigPh:overflow-y-scroll  landscapePhone:overflow-y-scroll landscapePhone:h-full landscapePhone:top-0 landscapePhone:translate-y-0 overscroll-none'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 0.8,
                    delay: showTip ? 3.5 : 0
                  }}
                  ref={textRef}
                >
                  <div className=' text-[50px]  NotoSerifB mb-[40px] tracking-[3px] landscapePad:text-[40px] portraitBigPh:text-[26px] portraitBigPh:mt-[50px]  portraitBigPh:mb-[24px] -translate-x-[10px] landscapePhone:mt-[50px] landscapePhone:text-[26px]'>
                    {zh.title}
                  </div>
                  <motion.div
                    className='w-full flex justify-end relative  '
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8, delay: 5 }}
                  >
                    <div className='text-[18px] laptop:text-[16px] NotoSerifR text-mainOrange-100 w-[60%] landscapePad:text-[16px] portraitBigPh:text-[15px] portraitBigPh:w-[75%] text-justify landscapePhone:text-[15px] '>
                      {zh.content}
                    </div>
                    <img
                      src={
                        !isMobile && isEnterHover
                          ? '../startImages/enter_btn_h.png'
                          : '../startImages/enter_btn_n.png'
                      }
                      alt='進入展場按鈕'
                      className='w-[160px] absolute bottom-0 right-[3%] translate-y-[250%] portraitPad:translate-y-[150%]   portraitBigPh:translate-y-[150%] portraitBigPh:w-[120px] portraitBigPh:pb-[40px] landscapePhone:translate-y-[150%] landscapePhone:pb-[40px] landscapePhone:w-[120px]  '
                      onClick={goVr}
                      onMouseEnter={() => {
                        uiState[event].isEnterHover = true
                        uiState.mouseStyle.showOrangeSpot = false
                      }}
                      onMouseLeave={() => {
                        uiState[event].isEnterHover = false
                        uiState.mouseStyle.showOrangeSpot = true
                      }}
                    />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              <motion.div
                className={`fixed left-[6%] z-30 flex items-center   landscapePad:left-[2%] landscapePad:bottom-[2%]  ${
                  mobileMode ? 'top-[3%]' : 'bottom-[5%]'
                } `}
                initial={{ opacity: 0 }}
                animate={{
                  opacity:
                    (scrollCount === 0 || scrollCount === 1) && showTip ? 1 : 0
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className='mr-4 portraitBigPh:mr-0'>
                  {isMusicPlaying ? (
                    <div className='w-[25px]'>
                      <div
                        className='h-[14px] w-[14px] flex mr-2  '
                        onClick={musicController}
                      >
                        <div className='h-full w-[5px] bg-black '></div>
                        <div className='flex-1 bg-transparent '></div>
                        <div className='h-full w-[5px] bg-black'></div>
                      </div>
                    </div>
                  ) : (
                    <div className='w-[25px]'>
                      <div
                        className='border-t-[8px] border-r-0 border-b-[8px]  border-l-[13.9px] border-transparent border-l-black '
                        onClick={musicController}
                      ></div>
                    </div>
                  )}
                </div>
                <div
                  onClick={musicController}
                  className=' NotoSerifR text-[16px] landscapePad:text-[15px] portraitBigPh:text-[13px] landscapePhone:text-[13px]  '
                >
                  <i className=' underline'>現正播映：純純 - 1934. 望春風</i>
                  <br />
                  <i className=' underline'>
                    Now Playing: Pure Pure - 1934. Bāng Tshun-hong
                  </i>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Homepage
